'use client';

import { faFacebook, faInstagram, faSnapchat, faTiktok, faXTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { DetailsRegistrationButton, IconText } from '@/components/atoms';
import { TeamLeagueDetailHeaderNavButtons } from '@/components/molecules';
import { TeamLeagueDetailsHeaderSkeleton } from '@/components/molecules/skeletons/TeamLeagueDetailsHeaderSkeleton';
import { useDistrictsInfoQuery, useSponsors } from '@/hooks/hooks';
import { useTeamLeagueDetailsQuery } from '@/hooks/team-leagues/useDetailsQuery';
import { useLeagueDivisionsQuery } from '@/hooks/divisions/useLeagueDivisionsQuery';
import { DateUtil } from '@/utils/date';
import { Button } from '@pickleballinc/react-ui';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { env } from '@env';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { useOrganizationStore } from '@/store/organization-store';
import { useDivisionsOptionsQuery } from '@/hooks/banner/useNextEventOrGameDayQuery';
import { NextGamedayOrEvent } from '@/components/molecules/NextGamedayOrEvent';
import { usePermissionStore } from '@/store/permission-store';
import { PermissionRole } from '@/entities/PermissionRoles';
import { TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { generateNavItems } from './TeamLeagueDetailHeaderNavButtons';
import { isLeagueRegistrationOpen } from '@/utils/helpers';
import { SocialMediaLinks } from '@/components/atoms/SocialMediaLinks';

interface TeamLeagueDetailsHeaderProps {
	teamLeagueId: string;
}

export const TeamLeagueDetailsHeader = ({ teamLeagueId }: TeamLeagueDetailsHeaderProps) => {
	const { canManageTeamLeague, userPermission } = usePermissionStore();
	const store = useOrganizationStore();
	const { data: leagueInfoResponse, isLoading } = useTeamLeagueDetailsQuery(teamLeagueId);
	const { data: nextGamedayOrEvent, isLoading: isLoadingNextGamedayOrEvent } = useDivisionsOptionsQuery(
		teamLeagueId,
		leagueInfoResponse?.data?.seasonUuid
	);
	const hasPermission = userPermission !== PermissionRole.NOT_AUTHORIZED;
	const { data: districts, isLoading: isLoadingDistrics } = useDistrictsInfoQuery(teamLeagueId);
	const { data: divisions, isLoading: isLoadingDivisions } = useLeagueDivisionsQuery(teamLeagueId, {});

	const { data: sponsorsInfo, isLoading: isLoadingSponsors } = useSponsors(teamLeagueId);

	const [isSticky, setIsSticky] = useState(false);
	const pathname = usePathname();
	const mainHeaderRef = useRef<HTMLDivElement>(null);

	const isUserPartOfLeague = leagueInfoResponse?.data?.isPartOfTeamLeague;

	const getLastPathSegment = (path: string) => {
		if (!path) return '';
		const segments = path.split('/').filter((segment: string) => segment !== '');
		if (segments.length === 0 || segments.length === 2) return '/';
		return '/' + segments[2];
	};

	const currentRoute = getLastPathSegment(pathname);

	const isRegistrationOpen = isLeagueRegistrationOpen(
		leagueInfoResponse?.data?.registrationDateOpen,
		leagueInfoResponse?.data?.registrationDateClose
	);

	const firstNavItem = generateNavItems(
		leagueInfoResponse?.data?.isSeasonActive ?? false,
		!districts?.data?.teamLeagueDistricts.length || districts?.data?.teamLeagueDistricts.length === 1,
		!divisions?.data?.length || divisions?.data?.length === 1,
		isRegistrationOpen,
		sponsorsInfo && sponsorsInfo?.length !== 0
	)[0];

	const isFirstNavItemRoute =
		(firstNavItem?.href === '/' && currentRoute === '/') || (firstNavItem?.href === '/standings' && currentRoute === '/standings');

	const useRegularStyling = !isFirstNavItemRoute;

	useEffect(() => {
		setIsSticky(false);
		if (!mainHeaderRef.current || currentRoute === '/division') return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry) {
					setIsSticky(!entry.isIntersecting && window.scrollY > 0);
				}
			},
			{
				threshold: 0,
				rootMargin: '0px 0px 0px 0px'
			}
		);

		observer.observe(mainHeaderRef.current);

		const checkScrollPosition = () => {
			if (window.scrollY === 0) {
				setIsSticky(false);
			}
		};

		window.addEventListener('scroll', checkScrollPosition);

		return () => {
			if (mainHeaderRef.current) {
				observer.unobserve(mainHeaderRef.current);
			}
			window.removeEventListener('scroll', checkScrollPosition);
		};
	}, [currentRoute, mainHeaderRef.current]);

	if (isLoading || isLoadingDistrics || isLoadingNextGamedayOrEvent || isLoadingSponsors || isLoadingDivisions) {
		return <TeamLeagueDetailsHeaderSkeleton />;
	}

	if (!leagueInfoResponse?.data || currentRoute === '/division') {
		return null;
	}

	const leagueInfo = leagueInfoResponse.data;

	const socialMediaLinks = [
		{ url: leagueInfo.facebook, icon: faFacebook },
		{ url: leagueInfo.twitter, icon: faXTwitter },
		{ url: leagueInfo.instagram, icon: faInstagram },
		{ url: leagueInfo.snapchat, icon: faSnapchat },
		{ url: leagueInfo.youtube, icon: faYoutube },
		{ url: leagueInfo.tiktok, icon: faTiktok }
	];

	return (
		<>
			<div ref={mainHeaderRef} className="bg-white shadow-md">
				<div className="flex flex-col p-4 gap-2.5 md:hidden">
					{!useRegularStyling && (
						<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines">{leagueInfo.title}</p>
					)}
					<Logo
						logoUrl={leagueInfo.logo?.url}
						title={leagueInfo.title}
						className={clsx('rounded-xl', !useRegularStyling ? 'sm:w-72 w-full' : 'sm:w-40 w-full')}
					/>
					{useRegularStyling && (
						<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines">{leagueInfo.title}</p>
					)}

					{!leagueInfo?.hideAllRegistrationInfo && (
						<RegistrationBadge isRegistrationOpen={isRegistrationOpen} registeredTeamsCount={leagueInfo.registeredTeamsCount} />
					)}

					{leagueInfo.dateStart && leagueInfo.dateEnd && <LeagueDate dateStart={leagueInfo.dateStart} dateEnd={leagueInfo.dateEnd} />}
					<TeamLeagueLinks />
					<SocialMediaLinks
						socialMediaLinks={socialMediaLinks}
						className="flex flex-wrap gap-4"
						linkClassName="w-6 h-6 flex items-center justify-center"
					/>
					{leagueInfo.useOnlineRegistration && (
						<DetailsRegistrationButton
							isRegistrationOpen={isRegistrationOpen}
							registrationEndDate={leagueInfo.registrationDateClose}
							isUserPartOfLeague={isUserPartOfLeague}
							useRegularStyling={!useRegularStyling}
							className="w-full lg:hidden"
						/>
					)}
					{canManageTeamLeague && (
						<a
							href={`${env.NEXT_PUBLIC_MANAGE_V1}/team-leagues/${store.teamLeagueUuid}/${leagueInfoResponse.data.organizationUuid}/settings`}
							className="w-full"
						>
							<Button variant="secondary" className={clsx('flex justify-center w-full gap-2 ', useRegularStyling && 'text-xs  py-1')}>
								<FontAwesomeIcon icon={faEdit} className="size-4" />
								Manage League
							</Button>
						</a>
					)}
					{hasPermission && (
						<Link href={`/team-leagues/${teamLeagueId}/team-info/active-teams`} className="w-full">
							<Button
								variant="secondary"
								className={clsx('flex justify-center w-full gap-2 px-4 ', useRegularStyling && 'text-xs py-1 px-2')}
							>
								<FontAwesomeIcon icon={faEdit} className="size-4" />
								Team Dashboard
							</Button>
						</Link>
					)}
				</div>
				{/* tablet & desktop */}
				<div className="hidden md:flex flex-col py-8 px-6 gap-2.5">
					{!useRegularStyling && (
						<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines">{leagueInfo.title}</p>
					)}
					<div className="flex justify-between">
						<div className="flex items-start gap-3">
							{leagueInfo.logo?.url && (
								<Logo
									logoUrl={leagueInfo.logo?.url}
									title={leagueInfo.title}
									className={clsx('rounded-xl', !useRegularStyling ? 'w-72' : 'w-40 h-24')}
								/>
							)}
							<div className={clsx('flex flex-col', !useRegularStyling ? 'gap-4' : 'gap-1.5')}>
								{!leagueInfo?.hideAllRegistrationInfo && !useRegularStyling && (
									<RegistrationBadge
										isRegistrationOpen={isRegistrationOpen}
										registeredTeamsCount={leagueInfo.registeredTeamsCount}
									/>
								)}
								{useRegularStyling && (
									<p className="text-gray-900 text-base font-semibold break-words whitespace-normal clamp-2-lines">
										{leagueInfo.title}
									</p>
								)}
								{leagueInfo.dateStart && leagueInfo.dateEnd && (
									<LeagueDate dateStart={leagueInfo.dateStart} dateEnd={leagueInfo.dateEnd} />
								)}
								<TeamLeagueLinks />
								<SocialMediaLinks
									socialMediaLinks={socialMediaLinks}
									className="flex-wrap gap-4 hidden xl:flex"
									linkClassName="w-6 h-6 flex items-center justify-center"
								/>
							</div>
						</div>
						{/* desktop screens */}
						<TeamLeagueActions
							leagueInfo={leagueInfo}
							isRegistrationOpen={isRegistrationOpen}
							isUserPartOfLeague={isUserPartOfLeague}
							canManageTeamLeague={canManageTeamLeague}
							hasPermission={hasPermission}
							teamLeagueId={teamLeagueId}
							isSticky={isSticky}
							useRegularStyling={useRegularStyling}
						/>
					</div>
					<SocialMediaLinks
						socialMediaLinks={socialMediaLinks}
						className="flex flex-wrap gap-4 xl:hidden"
						linkClassName="w-6 h-6 flex items-center justify-center"
					/>
					<div className="flex gap-4 xl:hidden">
						{leagueInfo.useOnlineRegistration && (
							<DetailsRegistrationButton
								isRegistrationOpen={isRegistrationOpen}
								registrationEndDate={leagueInfo.registrationDateClose}
								isUserPartOfLeague={isUserPartOfLeague}
								useRegularStyling={!useRegularStyling}
							/>
						)}
						{canManageTeamLeague && (
							<a
								href={`${env.NEXT_PUBLIC_MANAGE_V1}/team-leagues/${store.teamLeagueUuid}/${leagueInfoResponse.data.organizationUuid}/settings`}
							>
								<Button
									variant="secondary"
									className={clsx('flex justify-center w-full gap-2 h-full', useRegularStyling && 'text-xs py-1 px-2')}
								>
									{!useRegularStyling && <FontAwesomeIcon icon={faEdit} className="size-4" />}
									Manage League
								</Button>
							</a>
						)}

						{hasPermission && (
							<Link href={`/team-leagues/${teamLeagueId}/team-info/active-teams`}>
								<Button
									variant="secondary"
									className={clsx('flex justify-center w-full gap-2 h-full ', useRegularStyling && 'text-xs py-1 px-2')}
								>
									{!useRegularStyling && <FontAwesomeIcon icon={faEdit} className="size-4" />}
									Team Dashboard
								</Button>
							</Link>
						)}
					</div>
				</div>

				{nextGamedayOrEvent && nextGamedayOrEvent.statusCode === 200 && (
					<div className="px-4 pb-4 md:pb-8 md:px-6">
						<div
							className={clsx('grid gap-2 h-full', {
								'grid-cols-1': nextGamedayOrEvent.data?.length === 1,
								'grid-cols-1 md:grid-cols-2': nextGamedayOrEvent.data?.length > 1
							})}
						>
							{nextGamedayOrEvent.data.map((gamedayOrEvent) => (
								<NextGamedayOrEvent key={gamedayOrEvent.uuid} event={gamedayOrEvent} />
							))}
						</div>
					</div>
				)}

				<div className="w-full">
					<TeamLeagueDetailHeaderNavButtons
						hasPermission={hasPermission}
						teamLeagueId={teamLeagueId}
						shouldHideDistrics={!districts?.data?.teamLeagueDistricts.length || districts?.data?.teamLeagueDistricts.length === 1}
						shouldHideDivisions={!divisions?.data?.length || divisions?.data?.length === 1}
						isSeasonActive={leagueInfoResponse.data.isSeasonActive}
						isRegistrationOpen={isRegistrationOpen}
						isSeasonBtnVisible={sponsorsInfo && sponsorsInfo?.length !== 0}
					/>
				</div>
			</div>

			<div
				className={`fixed left-0 top-0 z-50 w-full bg-white shadow-md transition-transform duration-500 ${
					isSticky ? 'translate-y-0' : '-translate-y-full'
				}`}
			>
				<div className="mx-auto flex max-w-screen-xl flex-col flex-wrap gap-4 bg-white p-3 sm:p-4 sm:px-6">
					<div className="flex justify-between items-center">
						<div className={clsx('flex', !useRegularStyling ? 'gap-3' : 'gap-4')}>
							{leagueInfo.logo?.url && (
								<Logo
									logoUrl={leagueInfo.logo?.url}
									title={leagueInfo.title}
									className={clsx('rounded-md', !useRegularStyling ? 'w-40 h-24' : 'w-40')}
								/>
							)}
							<div className="flex flex-col gap-4">
								{useRegularStyling && (
									<div className="-mb-3 flex bg-white text-sm font-semibold text-gray-900 sm:text-base">{leagueInfo.title}</div>
								)}

								{!useRegularStyling && (
									<div className="-mb-3 flex bg-white text-base font-semibold text-gray-900 sm:text-lg">{leagueInfo.title}</div>
								)}
								{leagueInfo.dateStart && leagueInfo.dateEnd && (
									<LeagueDate dateStart={leagueInfo.dateStart} dateEnd={leagueInfo.dateEnd} />
								)}
								<SocialMediaLinks
									socialMediaLinks={socialMediaLinks}
									className="flex-wrap gap-4 hidden sm:flex"
									linkClassName="w-6 h-6 flex items-center justify-center"
								/>
							</div>
						</div>

						<TeamLeagueActions
							leagueInfo={leagueInfo}
							isRegistrationOpen={isRegistrationOpen}
							isUserPartOfLeague={isUserPartOfLeague}
							canManageTeamLeague={canManageTeamLeague}
							hasPermission={hasPermission}
							teamLeagueId={teamLeagueId}
							isSticky={isSticky}
							useRegularStyling={useRegularStyling}
						/>
					</div>
					<div className="sm:hidden flex gap-2 flex-col">
						{leagueInfo.useOnlineRegistration && (
							<DetailsRegistrationButton
								isRegistrationOpen={isRegistrationOpen}
								registrationEndDate={leagueInfo.registrationDateClose}
								isUserPartOfLeague={isUserPartOfLeague}
								useRegularStyling={!useRegularStyling}
								className="w-full lg:hidden"
							/>
						)}

						{canManageTeamLeague && (
							<a
								href={`${env.NEXT_PUBLIC_MANAGE_V1}/team-leagues/${store.teamLeagueUuid}/${leagueInfoResponse.data.organizationUuid}/settings`}
							>
								<Button
									variant="secondary"
									className={clsx('flex justify-center w-full gap-2 h-full', useRegularStyling && 'text-xs py-1 px-2')}
								>
									{!useRegularStyling && <FontAwesomeIcon icon={faEdit} className="size-4" />}
									Manage League
								</Button>
							</a>
						)}

						{hasPermission && (
							<Link href={`/team-leagues/${teamLeagueId}/team-info/active-teams`}>
								<Button
									variant="secondary"
									className={clsx('flex justify-center w-full gap-2 h-full ', useRegularStyling && 'text-xs py-1 px-2')}
								>
									{!useRegularStyling && <FontAwesomeIcon icon={faEdit} className="size-4" />}
									Team Dashboard
								</Button>
							</Link>
						)}
					</div>
				</div>

				<div className="w-full bg-gray-soft">
					<div className="mx-auto w-full max-w-screen-xl">
						<TeamLeagueDetailHeaderNavButtons
							hasPermission={hasPermission}
							teamLeagueId={teamLeagueId}
							shouldHideDistrics={!districts?.data?.teamLeagueDistricts.length || districts?.data?.teamLeagueDistricts.length === 1}
							shouldHideDivisions={!divisions?.data?.length || divisions?.data?.length === 1}
							isSeasonActive={leagueInfoResponse.data.isSeasonActive}
							isRegistrationOpen={isRegistrationOpen}
							isSeasonBtnVisible={sponsorsInfo && sponsorsInfo?.length !== 0}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

const Logo = ({ logoUrl, title, className }: { logoUrl?: string; title: string; className?: string }) => {
	return (
		<img
			className={clsx('object-contain transition-all duration-300', !logoUrl && 'h-[160px]', className ?? '')}
			src={logoUrl ?? `${process.env.NEXT_PUBLIC_IMAGE_CDN}/placeholder/pickleball_transparent_square.png`}
			alt={`${title} logo`}
		/>
	);
};

const RegistrationBadge = ({ isRegistrationOpen, registeredTeamsCount }: { isRegistrationOpen: boolean | null; registeredTeamsCount: number }) => {
	return (
		<div className="flex w-fit items-center rounded-md border border-gray-300 px-2 py-1 text-xs">
			<p className={`mr-1 size-[6px] rounded-full ${isRegistrationOpen ? ' bg-green-500' : ' bg-gray-300'}`}></p>
			<p className={`text-gray-600 ${isRegistrationOpen ? '' : 'text-gray-400'}`}>
				{isRegistrationOpen
					? `Reg open - ${registeredTeamsCount} team${registeredTeamsCount > 1 || !registeredTeamsCount ? 's' : ''}`
					: 'Registration closed'}
			</p>
		</div>
	);
};

const LeagueDate = ({ dateStart, dateEnd }: { dateStart: Date; dateEnd: Date }) => {
	return (
		<div className="flex items-center text-sm">
			<IconText className="mr-2 size-5" icon={faCalendarDays} />
			{DateUtil.formatCustom(dateStart, 'MMM DD, YYYY')} - {DateUtil.formatCustom(dateEnd, 'MMM DD, YYYY')}
		</div>
	);
};

const TeamLeagueLinks = () => {
	// Hidden for know, since backend team needs to add support for contact forms and tickets/website
	return (
		<div className="hidden flex-wrap gap-4">
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Contact Team League</p>
			</a>
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Tickets</p>
			</a>
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Website</p>
			</a>
			<a>
				<p className="text-sm underline font-medium text-gray-900 cursor-pointer">Share this team league</p>
			</a>
		</div>
	);
};

const TeamLeagueActions = ({
	leagueInfo,
	isRegistrationOpen,
	isUserPartOfLeague,
	canManageTeamLeague,
	hasPermission,
	teamLeagueId,
	isSticky,
	useRegularStyling
}: {
	leagueInfo: TeamLeagueInfo;
	isRegistrationOpen: boolean | null;
	isUserPartOfLeague: boolean | null | undefined;
	canManageTeamLeague: boolean;
	hasPermission: boolean;
	teamLeagueId: string;
	isSticky?: boolean;
	useRegularStyling: boolean;
}) => {
	const store = useOrganizationStore();

	return (
		<div className={clsx('flex-col', isSticky ? 'sm:flex hidden' : 'hidden xl:flex', !useRegularStyling ? 'gap-4' : 'gap-3 items-end')}>
			{leagueInfo.useOnlineRegistration && (
				<DetailsRegistrationButton
					isRegistrationOpen={isRegistrationOpen}
					registrationEndDate={leagueInfo.registrationDateClose}
					isUserPartOfLeague={isUserPartOfLeague}
					useRegularStyling={!useRegularStyling}
				/>
			)}

			{canManageTeamLeague && (
				<a href={`${env.NEXT_PUBLIC_MANAGE_V1}/team-leagues/${store.teamLeagueUuid}/${store.organizationUuid}/settings`} className="w-full">
					<Button
						variant="secondary"
						className={clsx('flex justify-center gap-2 w-full rounded-md', useRegularStyling ? 'py-1 px-2 text-xs' : '')}
					>
						{!useRegularStyling && <FontAwesomeIcon icon={faEdit} className="size-4" />}
						Manage League
					</Button>
				</a>
			)}
			{hasPermission && (
				<Link href={`/team-leagues/${teamLeagueId}/team-info/active-teams`} className="w-full">
					<Button
						variant="secondary"
						className={clsx('flex justify-center w-full gap-2 rounded-md', useRegularStyling ? 'text-xs py-1 px-2' : '')}
					>
						{!useRegularStyling && <FontAwesomeIcon icon={faEdit} className="size-4" />}
						Team Dashboard
					</Button>
				</Link>
			)}
		</div>
	);
};
